import { styled } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { useEffect, useMemo } from 'react';
import { selectedColumnsGridDisplayFieldsOrgState } from '../../AdminPanel/DataDisplay/DataDisplayState';
import { RESTRICTED_COLUMN_GROUPS } from '../../../components/ColumnManagement/constants';
import { shouldHideRestrictedColumnGroups } from '../../../components/ColumnManagement/utils';
import { GenericFallbacksWrapper } from '../../../components/Fallback/GenericFallbacksWrapper';
import { RecentCommentariesPanel } from '../components/ActivityDrawer/Commentaries/RecentCommentariesPanel';
import { CPWrapper } from '../components/CompanyProfilesWrapper';
import { selectedCompanyIdProfileAtom } from '../state/UIStateJ';
import { perfFrequencyAtom } from '../state/FinancialsDateViewPreferences';
import { ChartModal } from './components/Charts/ChartModal';
import { PerfChartsSkeletonLoader } from './components/Charts/PerfChartsSkeletonLoader';
import { PerformanceCharts } from './components/Charts/PerformanceCharts';
import { DateSettings2 } from './components/DateSettings/DateSettings';
import { MilestonesSection } from './components/Milestones/MilestonesSection';
import { ObjectivesSection } from './components/Objectives/ObjectivesSection';
import { RubricSection } from './components/Rubrics/RubricSection';
import { Statistics } from './components/Statistics/Statistics';

const HorizontalWrapper = styled('div')`
  height: min-content;
`;

export const CompanyProfilePerformance = () => {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const columnsToHide = useAtomValue(selectedColumnsGridDisplayFieldsOrgState);
  const resetFrequency = useSetAtom(perfFrequencyAtom(companyId));

  useEffect(() => {
    return () => resetFrequency(RESET);
  }, [resetFrequency]);

  const { hasRestrictedRubricsAccess, hasRestrictedKpisAccess } = useMemo(() => {
    const hiddenGroups = shouldHideRestrictedColumnGroups(
      [RESTRICTED_COLUMN_GROUPS.RUBRICS, RESTRICTED_COLUMN_GROUPS.KPIS],
      columnsToHide
    );
    return {
      hasRestrictedRubricsAccess: hiddenGroups[RESTRICTED_COLUMN_GROUPS.RUBRICS],
      hasRestrictedKpisAccess: hiddenGroups[RESTRICTED_COLUMN_GROUPS.KPIS],
    };
  }, [columnsToHide]);

  return (
    <CPWrapper key={companyId}>
      {!hasRestrictedKpisAccess && (
        <GenericFallbacksWrapper>
          <ChartModal />
        </GenericFallbacksWrapper>
      )}
      <DateSettings2 />

      {!hasRestrictedKpisAccess && (
        <GenericFallbacksWrapper>
          <Statistics />
        </GenericFallbacksWrapper>
      )}

      {!hasRestrictedKpisAccess && (
        <GenericFallbacksWrapper suspenseFallback={<PerfChartsSkeletonLoader />}>
          <PerformanceCharts />
        </GenericFallbacksWrapper>
      )}

      {!hasRestrictedRubricsAccess && (
        <GenericFallbacksWrapper>
          <RubricSection companyId={companyId} />
        </GenericFallbacksWrapper>
      )}

      <HorizontalWrapper>
        <GenericFallbacksWrapper>
          <RecentCommentariesPanel companyId={companyId} />
        </GenericFallbacksWrapper>
      </HorizontalWrapper>

      <GenericFallbacksWrapper>
        <ObjectivesSection />
      </GenericFallbacksWrapper>

      <GenericFallbacksWrapper>
        <MilestonesSection />
      </GenericFallbacksWrapper>
    </CPWrapper>
  );
};
